/**
 * CPA rule management — a SUB-PAGE of the Rocky Backend Management System.
 *
 * Route: `/admin-rbms/cpa`
 *
 * ## Why this is not an independent surface any more
 *
 * It started out shaped like `/ivt` and `/eval`: own layout, own access gate,
 * mounted outside the main `<AccessGate>`. Those two are built that way because
 * their audience is NOT the main-app audience — an `ivthub`-only user has to get in
 * without `rocky-user`.
 *
 * The design (Figma `cpa-liu`, 2026-10-29) puts CPA somewhere else entirely:
 *
 *     Rocky  >  Rocky Backend Management System  >  CPA
 *
 * i.e. a child of `/admin-rbms`, reusing Rocky's global icon rail rather than
 * growing its own nav. That makes the audience Rocky admins, so inheriting the main
 * gate is not a compromise — it is the correct gate, and a second one on different
 * groups would have denied the actual users.
 *
 * Consequence for the code: the gate and the nav rail are gone. Everything that
 * does the real work (projection, graphEdit, TreeCanvas, the inspectors) is
 * untouched — none of it knew where it was mounted.
 *
 * ## Stage gating lives on the ENTRY, not here
 *
 * The entry card on `/admin-rbms` is beta-only (`AdminRbmsPage.CARDS[].stages`).
 * This page deliberately is NOT: it is unfinished, not secret, so anyone with the
 * admin group and the URL may open it in any stage.
 *
 * Note the spelling: the mock labels this "CAP" in three places. The product is
 * Credit Program **A**dvisor, so CPA is correct and the mock has a typo.
 */
import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  BreadcrumbGroup,
  ContentLayout,
  Header,
  Tabs,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import CanvasPage from '../../pages/CanvasPage/CanvasPage';
import CpaPlaceholderPage from '../../pages/CpaPlaceholderPage/CpaPlaceholderPage';
import ProgramsPage from '../../pages/ProgramsPage/ProgramsPage';
import RulesPage from '../../pages/RulesPage/RulesPage';
import StackingPage from '../../pages/StackingPage/StackingPage';
import { canEditRules, readOnlyReason } from '../../utils/authorization';
import './CpaLayout.css';

const BASE = '/admin-rbms/cpa';

/** Tabs with a real page; the rest render CpaPlaceholderPage. */
const BUILT = new Set(['tree', 'programs', 'rules', 'stacking']);

/**
 * The five tabs, in the mock's order.
 *
 * The tree, 项目参数, 规则 and 叠加关系 are built (the last three read-only). 版本
 * is a placeholder that names the missing BACKEND capability rather than saying
 * "coming soon" — the blocker is a service that cannot yet store versions, not
 * unwritten UI.
 */
const TABS = [
  { id: 'tree', labelKey: 'tabTree' },
  { id: 'programs', labelKey: 'tabPrograms' },
  { id: 'rules', labelKey: 'tabRules' },
  { id: 'stacking', labelKey: 'tabStacking' },
  { id: 'versions', labelKey: 'tabVersions' },
] as const;

export default function CpaLayout() {
  const { t } = useTranslation(['cpa', 'settings']);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ldap_groups } = useAuth();

  const canEdit = useMemo(() => canEditRules(ldap_groups), [ldap_groups]);
  const roReason = useMemo(() => readOnlyReason(ldap_groups), [ldap_groups]);

  // Tab selection comes from the URL so a tab is linkable and the browser's back
  // button works through them.
  const activeTab = TABS.find((tb) => pathname.startsWith(`${BASE}/${tb.id}`))?.id ?? 'tree';

  return (
    <div className="cpa-page">
      <ContentLayout
        disableOverlap
        header={
          <div className="cpa-page-header">
            <BreadcrumbGroup
              items={[
                { text: t('settings:access.breadcrumbHome'), href: '/' },
                { text: t('settings:access.breadcrumbRbms'), href: '/admin-rbms' },
                { text: t('title'), href: BASE },
              ]}
              onFollow={(e) => {
                e.preventDefault();
                navigate(e.detail.href);
              }}
            />
            <Header variant="h1">{t('title')}</Header>
          </div>
        }
      >
        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => navigate(`${BASE}/${detail.activeTabId}`)}
          tabs={TABS.map((tb) => ({
            id: tb.id,
            label: t(tb.labelKey),
            // Content is rendered by the router below rather than here, so a tab's
            // page keeps its own URL instead of living inside tab state.
            content: null,
          }))}
        />

        <Routes>
          <Route index element={<Navigate to={`${BASE}/tree`} replace />} />
          <Route
            path="tree"
            element={<CanvasPage canEdit={canEdit} readOnlyReason={roReason} />}
          />
          <Route path="programs" element={<ProgramsPage />} />
          <Route path="rules" element={<RulesPage />} />
          <Route path="stacking" element={<StackingPage />} />
          {TABS.filter((tb) => !BUILT.has(tb.id)).map((tb) => (
            <Route
              key={tb.id}
              path={tb.id}
              element={<CpaPlaceholderPage page={t(tb.labelKey)} pageId={tb.id} />}
            />
          ))}
          <Route path="*" element={<Navigate to={`${BASE}/tree`} replace />} />
        </Routes>
      </ContentLayout>
    </div>
  );
}
